import { formatDate, localizeNumber } from "../../Helper/generalhelper";
import { padToThreeDigits } from "../../Helper/PadToThreeDigits";
import { valuesAreEquivalent } from "./scoopChangedFields";

const JOB_LOG_STATUSES = new Set([
  "job",
  "job update",
  "job update linguist",
  "task",
  "report -job",
]);

const JOB_LOG_TYPES = new Set([
  "job",
  "job linguist",
  "job status update",
  "job status updated",
  "job project manager update",
  "project manager updated",
  "job due date update",
  "due date updated",
  "job resource updated",
  "job moved",
]);

const HIDDEN_JOB_KEYS = new Set([
  "updated_date",
  "user_base_currency_rate",
  "job_summmeryId",
  "created_date",
  "order_id",
  "job_id",
  "accept",
  "rejection",
  "request_rejected_ids",
]);

// Labels from EditJob.js / WorkInstruction.js.
const JOB_FIELD_LABELS = {
  item_status: "Status",
  contact_person: "Project Manager",
  resource: "Resources",
  item_id: "Scoop",
  ItemLanguage: "Language",
  due_date: "Due Date",
  description: "Comment",
  job_payment_note: "Job Payment Note",
  work_instruction: "Work Instructions",
  price: "Price lines",
  total_price: "Prices",
};

const LEGACY_FIELD_BY_LOG = {
  "job project manager update": "contact_person",
  "project manager updated": "contact_person",
  "job status update": "item_status",
  "job status updated": "item_status",
  "job due date update": "due_date",
  "due date updated": "due_date",
  "job resource updated": "resource",
  "job moved": "item_id",
};

const PREVIEW_LIMIT = 4;

export function isJobLog(logStatus, logType) {
  if (JOB_LOG_STATUSES.has(String(logStatus || "").trim().toLowerCase())) return true;
  return JOB_LOG_TYPES.has(String(logType || "").trim().toLowerCase());
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function looksLikeJson(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function parseMaybeJson(value) {
  if (!looksLikeJson(value)) return value;
  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return value;
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  return false;
}

function isZeroDate(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim().startsWith("0000-00-00");
}

function dueDateForCompare(value) {
  return isZeroDate(value) ? "" : value;
}

function lookupName(value, nameMap) {
  if (isEmptyValue(value)) return "";
  const id = String(value).trim();
  return nameMap?.[id] || id;
}

function formatScoopNumber(value) {
  if (isEmptyValue(value)) return "";
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return String(value);
  return padToThreeDigits(parsed);
}

function formatPriceLines(value) {
  const parsed = parseMaybeJson(value);
  if (!parsed || (Array.isArray(parsed) && !parsed.length)) return "";
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  return rows
    .map(row => {
      if (!row || typeof row !== "object") return String(row ?? "");
      const quantity = row.quantity ?? "";
      const priceUnit = row.pricelist ?? row.priceUnit ?? "";
      const unitPrice = isEmptyValue(row.itemPrice) ? "" : localizeNumber(row.itemPrice);
      const prices = isEmptyValue(row.itemTotal) && isEmptyValue(row.amtSum)
        ? ""
        : localizeNumber(row.itemTotal ?? row.amtSum);
      return `Quantity: ${quantity}, Price Unit: ${priceUnit}, Unit Price: ${unitPrice}, Prices: ${prices}`;
    })
    .join("\n");
}

function formatWorkInstructions(value) {
  const parsed = parseMaybeJson(value);
  if (!parsed || (Array.isArray(parsed) && !parsed.length)) return "";
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  return rows
    .map(row => {
      if (!row || typeof row !== "object") return String(row ?? "");
      return row.work_name || "";
    })
    .filter(Boolean)
    .join("\n");
}

export function formatJobFieldValue(fieldKey, value, lookups = {}) {
  if (fieldKey === "item_status") return lookupName(value, lookups.jobStatusNames);
  if (fieldKey === "contact_person" || fieldKey === "resource") {
    return lookupName(value, lookups.userNames);
  }
  if (fieldKey === "item_id") return formatScoopNumber(value);
  if (fieldKey === "price") return formatPriceLines(value);
  if (fieldKey === "work_instruction") return formatWorkInstructions(value);
  if (fieldKey === "total_price") {
    if (isEmptyValue(value)) return "";
    return String(localizeNumber(value));
  }
  if (fieldKey === "due_date") {
    if (isEmptyValue(value) || isZeroDate(value)) return "";
    return formatDate(String(value).trim(), { includeTime: true });
  }
  if (isEmptyValue(value)) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function legacyChangedFields(details, logType) {
  const parsed = parseDetails(details);
  if (
    !parsed ||
    (!Object.prototype.hasOwnProperty.call(parsed, "oldValue") &&
      !Object.prototype.hasOwnProperty.call(parsed, "newValue"))
  ) {
    return null;
  }
  const fieldKey = LEGACY_FIELD_BY_LOG[String(logType || "").trim().toLowerCase()];
  if (!fieldKey) return null;
  return {
    [fieldKey]: {
      old: parsed.oldValue,
      new: parsed.newValue,
    },
  };
}

export function getJobChangedFields(details, logType, lookups = {}) {
  const changedFields = parseChangedFields(details) || legacyChangedFields(details, logType);
  if (!changedFields) return [];

  return Object.keys(changedFields)
    .filter(key => JOB_FIELD_LABELS[key] && !HIDDEN_JOB_KEYS.has(key))
    .filter(key => {
      const change = changedFields[key];
      if (!change || typeof change !== "object" || Array.isArray(change)) return false;
      if (!Object.prototype.hasOwnProperty.call(change, "old") && !Object.prototype.hasOwnProperty.call(change, "new")) {
        return false;
      }
      const oldValue = key === "due_date" ? dueDateForCompare(change.old) : change.old;
      const newValue = key === "due_date" ? dueDateForCompare(change.new) : change.new;
      return !valuesAreEquivalent(oldValue, newValue, key);
    })
    .map(key => ({
      key,
      label: JOB_FIELD_LABELS[key],
      old: formatJobFieldValue(key, changedFields[key].old, lookups),
      new: formatJobFieldValue(key, changedFields[key].new, lookups),
    }));
}

export function getJobChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
